import { useCallback } from 'react';
import { ContentCollection, CollectionItem } from '../../../hooks/useContentCollections';
import { buildItemsPayload, getCollectionClientIds, selectionToUpdatePayload } from '../utils/collection-helpers';
import { AddableCollectionItem } from '../../../components/content-library/add-collection-item-modal';

interface UseCollectionOperationsProps {
  selectedCollection: ContentCollection | undefined;
  updateCollection: (args: { collectionId: number; payload: any }) => Promise<any>;
}

export const useCollectionOperations = ({ selectedCollection, updateCollection }: UseCollectionOperationsProps) => {
  const handleItemAdd = useCallback(
    async (selection: AddableCollectionItem) => {
      if (!selectedCollection) return;

      try {
        const existingItemsPayload = buildItemsPayload(selectedCollection.items);
        const nextSortOrder = existingItemsPayload.length;
        const newEntry = selectionToUpdatePayload(selection, nextSortOrder);
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { items: [...existingItemsPayload, newEntry] },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleItemRemove = useCallback(
    async (itemId: number) => {
      if (!selectedCollection) return;

      try {
        const remainingItems = selectedCollection.items.filter((item) => item.id !== itemId);
        const payloadItems = buildItemsPayload(remainingItems);
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { items: payloadItems },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleItemReorder = useCallback(
    async (reorderedItems: CollectionItem[]) => {
      if (!selectedCollection) return;

      try {
        const payloadItems = buildItemsPayload(reorderedItems);
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { items: payloadItems },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateField = useCallback(
    async (field: string, value: any) => {
      if (!selectedCollection) return;

      try {
        await updateCollection({
          collectionId: selectedCollection.id,
          payload: { [field]: value },
        });
      } catch (error) {
        // handled by mutation toast
      }
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateVerticals = useCallback(
    async (nextVerticalIds: number[]) => {
      if (!selectedCollection) return;

      const currentIds = selectedCollection.verticals.map((vertical) => vertical.id);
      const sortedCurrent = [...currentIds].sort((a, b) => a - b);
      const sortedNext = [...nextVerticalIds].sort((a, b) => a - b);

      const hasChanged =
        sortedCurrent.length !== sortedNext.length || sortedCurrent.some((id, index) => id !== sortedNext[index]);

      if (!hasChanged) return;

      // Match create modal: setting verticals clears client assignments.
      const payload: Record<string, unknown> = { vertical_ids: nextVerticalIds };
      if (nextVerticalIds.length > 0 && getCollectionClientIds(selectedCollection).length > 0) {
        payload.client_ids = [];
        if (selectedCollection.is_public) {
          payload.is_public = false;
        }
      }
      await updateCollection({
        collectionId: selectedCollection.id,
        payload,
      });
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateTags = useCallback(
    async (nextTagIds: number[]) => {
      if (!selectedCollection) return;

      const currentIds = selectedCollection.tags.map((tag) => tag.id);
      const sortedCurrent = [...currentIds].sort((a, b) => a - b);
      const sortedNext = [...nextTagIds].sort((a, b) => a - b);

      const hasChanged =
        sortedCurrent.length !== sortedNext.length || sortedCurrent.some((id, index) => id !== sortedNext[index]);

      if (!hasChanged) return;

      await handleUpdateField('tag_ids', nextTagIds.length > 0 ? nextTagIds : null);
    },
    [selectedCollection, handleUpdateField],
  );

  const handleUpdateDomain = useCallback(
    async (domainId: number | null) => {
      if (!selectedCollection) return;

      const currentDomainId = selectedCollection.domain_id ?? null;
      if (currentDomainId === domainId) return;

      // Match create modal: setting a domain clears client assignments.
      const payload: Record<string, unknown> = { domain_id: domainId };
      if (domainId != null && getCollectionClientIds(selectedCollection).length > 0) {
        payload.client_ids = [];
        if (selectedCollection.is_public) {
          payload.is_public = false;
        }
      }
      await updateCollection({
        collectionId: selectedCollection.id,
        payload,
      });
    },
    [selectedCollection, updateCollection],
  );

  const handleUpdateClient = useCallback(
    async (clientIds: number[]) => {
      if (!selectedCollection) return;

      const currentIds = getCollectionClientIds(selectedCollection);
      const sortedCurrent = [...currentIds].sort((a, b) => a - b);
      const sortedNext = [...clientIds].sort((a, b) => a - b);
      const hasChanged =
        sortedCurrent.length !== sortedNext.length || sortedCurrent.some((id, index) => id !== sortedNext[index]);
      if (!hasChanged) return;

      // Match create modal: setting clients clears domain + verticals.
      const payload: Record<string, unknown> = { client_ids: clientIds };
      if (clientIds.length > 0) {
        payload.domain_id = null;
        payload.vertical_ids = [];
      }
      // Clearing the last client must also disable public embed (checkbox is otherwise stuck).
      if (clientIds.length === 0 && selectedCollection.is_public) {
        payload.is_public = false;
      }
      await updateCollection({
        collectionId: selectedCollection.id,
        payload,
      });
    },
    [selectedCollection, updateCollection],
  );

  return {
    handleItemAdd,
    handleItemRemove,
    handleItemReorder,
    handleUpdateField,
    handleUpdateVerticals,
    handleUpdateTags,
    handleUpdateDomain,
    handleUpdateClient,
  };
};
